import React, { useEffect, useState } from 'react';
import { NextPage } from 'next';
import findIndex from 'lodash/findIndex';
import { Button, ButtonLink } from '@blueprint/button';
import { Text } from '@blueprint/text';
import { Link } from '@blueprint/link';
import { Checkbox } from '@blueprint/checkbox';
import { SaveOutlineSmall, CloseSmall } from '@blueprint/icons';
import Head from 'shared/components/Head/Head';
import { trackPage } from 'shared/helpers/trackPage';
import {
    Tab,
    Tabs
} from 'listing-details/components/SavedListingsTabs/SavedListingsTabs';
import {
    Layout,
    NumberOfProperties,
    FiltersContainer,
    PropertiesContainer,
    SavedListingsHeader,
    CenteredBox,
    StyledBanner,
    InstructionText,
    PropertyNotesNotice,
    Loader
} from './SavedListingsPage.styles';
import { SavedListingCard } from 'listing-details/components/SavedListingCard/SavedListingCard';
import {
    useDeleteSavedListing,
    useSavedListings
} from '../../hooks/useSavedListings/useSavedListings';
import { useSelectedSection } from './SavedListingsPage.hooks';
import HouseWithTree from './house-with-tree.svg';
import Spinner from 'public/assets/spinner.svg';
import { Banner } from '@blueprint/ui';
import {
    trackNewSearch,
    trackShowExpired,
    trackPropertyNotesLink
} from './SavedListingsPage.tracking';
import { isLiveListing } from './SavedListingsPage.helpers';

const PropertyCount = ({ count = 0 }) => (
    <NumberOfProperties size="7">{count} properties</NumberOfProperties>
);

const SavedListingsPage: NextPage = () => {
    const {
        savedListings,
        loading,
        savedListingsBySection,
        orderedFilteredSavedListingsBySection,
        hasError
    } = useSavedListings();

    const { selectedSection, onSectionChange } = useSelectedSection(
        savedListings,
        savedListingsBySection,
        orderedFilteredSavedListingsBySection
    );

    const {
        deleteSavedListing,
        error: deleteError,
        closeError
    } = useDeleteSavedListing();

    const [isShowExpired, setShowExpired] = useState(false);

    useEffect(() => {
        // Note: LEX-674: this is intentionally the legacy URL for tracking purposes
        trackPage('/myaccount/favourites/listings/');
    }, []);

    const onExpiredFilterChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const isEnabled = event.target.checked;

        trackShowExpired(isEnabled);
        setShowExpired(isEnabled);
    };

    let savedListingContent = null;

    if (loading) {
        savedListingContent = (
            <Loader src={Spinner} alt="loading" data-testid="load-spinner" />
        );
    } else if (hasError) {
        savedListingContent = (
            <>
                <PropertyCount />
                <Banner
                    type="failure"
                    strapline="Something has gone wrong"
                    title="Problem loading saved properties"
                    text="We cannot load your saved properties right now, please refresh the page or try again later."
                    data-testid="fetch-error-banner"
                />
            </>
        );
    } else if (!savedListings?.length) {
        savedListingContent = (
            <>
                <PropertyCount />
                <Text
                    isBold
                    size="6"
                    data-testid="saved-properties-empty-message"
                >
                    You don&apos;t have any saved properties yet!
                </Text>
                <InstructionText>
                    Click the <b>Save icon</b>&nbsp;
                    <SaveOutlineSmall /> on any property and we&apos;ll save it
                    here for you to view later
                </InstructionText>
                <CenteredBox>
                    <img
                        src={HouseWithTree}
                        alt="House with tree"
                        data-testid="house-with-tree-image"
                    />
                    <ButtonLink
                        isFullWidth
                        href="/"
                        data-testid="start-search-button"
                        onClick={trackNewSearch}
                    >
                        Start a search
                    </ButtonLink>
                </CenteredBox>
            </>
        );
    } else if (
        savedListingsBySection?.[selectedSection] &&
        orderedFilteredSavedListingsBySection
    ) {
        const listingsForSection = savedListingsBySection[selectedSection];
        const listingsForSectionWithoutExpired = listingsForSection.filter(
            isLiveListing
        );
        const listings = isShowExpired
            ? listingsForSection
            : listingsForSectionWithoutExpired;

        const expiredListingTotal =
            listingsForSection.length - listingsForSectionWithoutExpired.length;

        savedListingContent = (
            <>
                <PropertyCount count={savedListings.length} />
                <Tabs
                    selectedIndex={findIndex(
                        orderedFilteredSavedListingsBySection,
                        (section) => section.key === selectedSection
                    )}
                    onTabSelected={(index) =>
                        onSectionChange(
                            orderedFilteredSavedListingsBySection[index].key
                        )
                    }
                >
                    {orderedFilteredSavedListingsBySection.map((section) => {
                        return (
                            <Tab
                                href={`?section=${section.key}`}
                                key={section.key}
                                testId={`section-tab-${section.key}`}
                            >
                                {section.label}
                            </Tab>
                        );
                    })}
                </Tabs>
                {expiredListingTotal > 0 && (
                    <FiltersContainer>
                        <Checkbox
                            label={`Show expired listings (${expiredListingTotal})`}
                            id="expired-listings-checkbox"
                            onChange={onExpiredFilterChange}
                            checked={isShowExpired}
                        />
                    </FiltersContainer>
                )}
                <PropertiesContainer
                    data-testid="properties-container"
                    aria-live="polite"
                >
                    {Boolean(listings?.length) &&
                        listings.map((listing) => (
                            <SavedListingCard
                                deleteListing={() => {
                                    deleteSavedListing(listing.listingId);
                                }}
                                listing={listing}
                                key={`listing-${listing.listingId}`}
                            />
                        ))}
                </PropertiesContainer>
                <PropertyNotesNotice>
                    Looking for your property notes? You can see and manage them
                    all in the{' '}
                    <Link
                        href="/myaccount/my-notes/"
                        onClick={trackPropertyNotesLink}
                        data-testid="property-notes-link"
                    >
                        property notes area
                    </Link>
                    .
                </PropertyNotesNotice>
            </>
        );
    }

    return (
        <>
            <Head
                canonicalPath="/favourites/properties/"
                pageTitle="Saved properties"
                pageDescription=""
                index={() => false}
            />
            <Layout id="main-content" data-testid="saved-listings-page">
                <SavedListingsHeader size="5" data-testid="main-header">
                    Saved properties
                </SavedListingsHeader>
                {savedListingContent}
                {deleteError ? (
                    <StyledBanner
                        type="failure"
                        strapline="Something has gone wrong"
                        title="Problem removing your saved property"
                        text="We cannot remove your saved property right now. Please refresh the page or try again later."
                        data-testid="delete-error-banner"
                    >
                        <Button
                            variant="tertiary"
                            iconOnly
                            onClick={closeError}
                        >
                            <CloseSmall />
                        </Button>
                    </StyledBanner>
                ) : null}
            </Layout>
        </>
    );
};

export default SavedListingsPage;
